import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { t, LOCALES } from '../lib/i18n';
import { APP_BUILD_SHORT } from '../lib/version';
import { api } from '../lib/api';
import { videoEmbedUrl, looksLikeImageUrl } from '../lib/media';
import {
  ShieldCheck, FileText, Activity, Globe2, Star, Quote, Stethoscope,
  CalendarDays, Video, Sparkles, ClipboardCheck, Lock, Languages, Check,
  MapPin, Phone, Mail, Clock, ChevronDown, Users2, Layers, Play, Pause
} from 'lucide-react';
import HeroPortrait from '../components/HeroPortrait';
import { BrandLogo } from '../components/BrandLogo';
import HeroPhoneCarousel from '../components/hero/HeroPhoneCarousel';
import { ComplianceShieldClipart } from '../components/BrandClipart';
import BookingWizard from '../components/BookingWizard';
import InstallAppCTA from '../components/InstallAppCTA';
import AppAndVoicesSection from '../components/AppAndVoicesSection';
import PatientVoices from '../components/PatientVoices';
import { BrandName } from '../components/BrandName';

const FEATURES = [
  { icon: Stethoscope, title: 'Every discipline', body: 'Physiotherapy, osteopathy, chiropractic, massage therapy and fitness & wellness — one chart, one schedule, one platform.' },
  { icon: CalendarDays, title: 'Patient self-booking', body: 'Real-time slot picker, prep-instructions modal and 409 conflict guard so no slot is double-booked.' },
  { icon: Sparkles, title: 'AI Scribe + red-flag screening', body: 'Whisper transcribes your session, Claude reads each intake and surfaces clinical contraindications before the first visit.' },
  { icon: FileText, title: 'Medical record storage', body: 'Emergent Object Storage with patient-side upload, 90-day garbage collection, full audit trail and EU 20-year retention.' },
  { icon: ClipboardCheck, title: 'Outcome measures + body chart', body: 'Built-in Oswestry, QuickDASH, NDI, VAS — auto-scored with trend graphs. 2D anatomical muscle chart with individual selection.' },
  { icon: Video, title: 'Telehealth + send-to-patient PDFs', body: 'One-click Jitsi room per appointment. Email treatment plans, fitness programs and HEP videos as PDFs from the chart.' },
  { icon: Lock, title: 'HIPAA + GDPR ready', body: 'Profile-completion gate, country-aware ID labels (PESEL · SSN · NI · …), Resend magic-links and bcrypt session tokens.' },
  { icon: Languages, title: 'EN · PL · ES + multi-clinic', body: 'Run a single solo clinic or a multi-clinic SaaS with subdomain routing, Stripe Connect and per-clinic feature toggles.' },
];

export default function Landing() {
  const { user, locale, setLocale } = useAuth();
  const [settings, setSettings] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [theme, setTheme] = useState({});
  const [showWizard, setShowWizard] = useState(false);
  const [heroScroll, setHeroScroll] = useState(0);
  const [heroHover, setHeroHover] = useState(false);
  const [heroPaused, setHeroPaused] = useState(false);
  const heroIframeRef = useRef(null);
  const heroVideoRef = useRef(null);

  const toggleHeroVideo = () => {
    const next = !heroPaused;
    if (heroVideoRef.current) {
      next ? heroVideoRef.current.pause() : heroVideoRef.current.play();
    }
    if (heroIframeRef.current?.contentWindow) {
      heroIframeRef.current.contentWindow.postMessage(JSON.stringify({
        event: 'command', func: next ? 'pauseVideo' : 'playVideo', args: [],
      }), '*');
    }
    setHeroPaused(next);
  };
  // Iter 113h — slideshow REMOVED, single static variant. No more mouth/face
  // micro-shift between AI-generated variants. Holding on female-1 (Patient Chart).
  const femaleVariant = 1;

  useEffect(() => {
    api.get('/clinic/settings').then(r => setSettings(r.data));
    api.get('/reviews/public?limit=6').then(r => setReviews(r.data || [])).catch(() => {});
    api.get('/theme/active').then(r => setTheme(r.data || {})).catch(() => {});
  }, []);

  // Iter 104 — subtle scroll-triggered parallax on the hero portrait pair.
  // Honours `prefers-reduced-motion` for accessibility. Caps drift at 600px
  // of scroll so the effect doesn't get exaggerated on long pages.
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (mq.matches) return undefined;
    let raf = null;
    const onScroll = () => {
      if (raf) return;
      raf = window.requestAnimationFrame(() => {
        setHeroScroll(Math.min(window.scrollY || 0, 600));
        raf = null;
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (raf) window.cancelAnimationFrame(raf);
    };
  }, []);

  return (
    <div className="min-h-screen" style={{ background: theme.background_color || '#FAF6EE', color: '#0F1B2D' }}>
      {/* Seasonal theme override — admin-toggleable hero overlay */}
      {theme?.name && (theme.hero_image_url || theme.hero_video_url) && (
        <div className="relative bg-[#0F1B2D]" data-testid="seasonal-theme-banner">
          {theme.hero_video_url ? (
            videoEmbedUrl(theme.hero_video_url) ? (
              <iframe
                ref={heroIframeRef}
                src={videoEmbedUrl(theme.hero_video_url)}
                title={theme.name}
                className="w-full h-64 md:h-80 pointer-events-none"
                frameBorder="0"
                allow="autoplay; encrypted-media; picture-in-picture"
              />
            ) : (
              <video ref={heroVideoRef} src={theme.hero_video_url} autoPlay muted loop playsInline
                     className="w-full h-64 md:h-80 object-cover"
                     onError={e => { e.currentTarget.style.display = 'none'; }}/>
            )
          ) : (theme.hero_image_url?.startsWith('data:') || looksLikeImageUrl(theme.hero_image_url)) ? (
            <img src={theme.hero_image_url} alt={theme.name}
                 className="w-full h-64 md:h-80 object-cover"
                 onError={e => { e.currentTarget.style.display = 'none'; }}/>
          ) : (
            <div className="w-full h-64 md:h-80" style={{ background: theme.background_color || '#0F1B2D' }}/>
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent flex items-end p-6 md:p-10 pointer-events-none">
            <div>
              {theme.hero_title && <h2 className="text-3xl md:text-5xl font-heading font-bold text-white">{theme.hero_title}</h2>}
              {theme.hero_tagline && <p className="text-white/90 mt-2 text-sm md:text-base">{theme.hero_tagline}</p>}
            </div>
          </div>
          {/* Iter 144 — small pause/play control for the hero video. */}
          {theme.hero_video_url && (
            <button
              onClick={toggleHeroVideo}
              className="absolute bottom-4 right-4 z-10 w-9 h-9 rounded-full bg-black/50 hover:bg-black/70 backdrop-blur border border-white/30 text-white flex items-center justify-center transition-colors"
              title={heroPaused ? 'Play video' : 'Pause video'}
              aria-label={heroPaused ? 'Play hero video' : 'Pause hero video'}
              data-testid="hero-video-toggle"
            >
              {heroPaused ? <Play className="w-4 h-4 ml-0.5"/> : <Pause className="w-4 h-4"/>}
            </button>
          )}
          {theme.music_url && (
            <audio src={theme.music_url} autoPlay loop className="hidden"/>
          )}
        </div>
      )}
      {/* Header */}
      <header className="sticky top-0 z-50 w-full backdrop-blur-xl bg-white/85 border-b border-[#E8DFC8]">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3" data-testid="brand-logo">
            {/* Iter 133 — This is the PUBLIC Physiogenic marketing page. It
                must NEVER swap in a subscriber clinic's custom logo/name
                even when the visitor is logged into their tenant. Locked
                to the platform brand assets on purpose. */}
            <BrandLogo size={80} showCopyright/>
            <span className="hidden sm:inline font-heading font-bold tracking-tight text-xl"><BrandName/></span>
          </div>
          <nav className="hidden md:flex items-center gap-8 text-sm font-medium text-[#3A4A5E]">
            <a href="#features" className="hover:text-[#003D80] transition" data-testid="nav-features">{t(locale, 'nav_features')}</a>
            <a href="#compliance" className="hover:text-[#003D80] transition" data-testid="nav-compliance">{t(locale, 'nav_compliance')}</a>
            <a href="#voices" className="hover:text-[#003D80] transition" data-testid="nav-reviews">Reviews</a>

            {/* Iter 127 — "About" dropdown. CSS-only via `group` + `group-hover`
                to avoid any state or click-outside plumbing. Keyboard users can
                focus the trigger and reach the menu via Tab (focus-within
                mirrors group-hover for a11y). */}
            <div className="relative group" data-testid="nav-about-dropdown">
              <button
                type="button"
                className="inline-flex items-center gap-1 hover:text-[#003D80] transition focus:outline-none focus-visible:text-[#003D80]"
                data-testid="nav-about-trigger"
                aria-haspopup="true"
                aria-expanded="false"
              >
                {t(locale, 'nav_about') || 'About'}
                <ChevronDown className="w-3.5 h-3.5 mt-0.5 transition-transform group-hover:rotate-180"/>
              </button>
              <div
                className="absolute left-0 top-full pt-3 opacity-0 pointer-events-none translate-y-1 transition-all duration-150 group-hover:opacity-100 group-hover:pointer-events-auto group-hover:translate-y-0 group-focus-within:opacity-100 group-focus-within:pointer-events-auto group-focus-within:translate-y-0 z-50"
                data-testid="nav-about-menu"
              >
                <div className="w-[320px] rounded-xl border border-[#E8DFC8] bg-white shadow-2xl overflow-hidden">
                  <Link
                    to="/about"
                    className="flex items-start gap-3 px-4 py-3.5 hover:bg-[#FAF6EE] transition"
                    data-testid="nav-about-us-link"
                  >
                    <div className="w-8 h-8 rounded-lg bg-[#FFB000]/15 text-[#8A5A00] grid place-items-center shrink-0 mt-0.5">
                      <Users2 className="w-4 h-4"/>
                    </div>
                    <div>
                      <div className="font-semibold text-[#1A2332]">{t(locale, 'nav_about_us') || 'About us'}</div>
                      <div className="text-xs text-[#3A4A5E] mt-0.5">Who we are, our mission, our goals</div>
                    </div>
                  </Link>
                  <div className="h-px bg-[#E8DFC8]"/>
                  <Link
                    to="/platform"
                    className="flex items-start gap-3 px-4 py-3.5 hover:bg-[#FAF6EE] transition"
                    data-testid="nav-platform-link"
                  >
                    <div className="w-8 h-8 rounded-lg bg-[#003D80]/10 text-[#003D80] grid place-items-center shrink-0 mt-0.5">
                      <Layers className="w-4 h-4"/>
                    </div>
                    <div>
                      <div className="font-semibold text-[#1A2332]">{t(locale, 'nav_platform') || 'Allied Healthcare Platform'}</div>
                      <div className="text-xs text-[#3A4A5E] mt-0.5">Every module — Booking · AI · Posture · Tax · more</div>
                    </div>
                  </Link>
                </div>
              </div>
            </div>
          </nav>
          <div className="flex items-center gap-2 md:gap-3">
            <select
              value={locale}
              onChange={(e) => setLocale(e.target.value)}
              data-testid="landing-locale-select"
              className="text-sm bg-transparent border border-[#E8DFC8] rounded-md px-2 py-1.5 outline-none cursor-pointer hover:border-[#003D80] transition"
            >
              {LOCALES.map((l) => <option key={l.code} value={l.code}>{l.label}</option>)}
            </select>
            {user ? (
              /* Signed-in visitor — send them to their role dashboard. */
              <Link to={user.role === 'admin' || user.role === 'clinician' ? '/admin' : '/patient'}
                    className="px-5 py-2 rounded-lg bg-[#003D80] text-white text-sm font-semibold hover:bg-[#002B5C] transition shadow-sm"
                    data-testid="go-app-button">
                {t(locale, 'dashboard')}
              </Link>
            ) : (
              /* Iter 122 — one unified "Sign in" link (was "Staff sign in"). Routes
                 by role automatically. "Get started" CTA removed from header per
                 user request; onboarding still reachable via footer + /start-clinic. */
              <Link to="/sign-in"
                    className="px-5 py-2 rounded-lg bg-[#003D80] text-white text-sm font-semibold hover:bg-[#002B5C] transition shadow-sm"
                    data-testid="signin-link">
                {t(locale, 'auth_login') || 'Sign in'}
              </Link>
            )}
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative py-12 md:py-16 lg:py-20 px-6 md:px-10 overflow-hidden" style={{ background: '#003D80' }}>
        {/* Soft amber + lighter blue radial glows on dark bg */}
        <div aria-hidden="true" className="absolute inset-0 pointer-events-none"
             style={{ background: 'radial-gradient(ellipse at 70% 30%, rgba(255,176,0,0.10), transparent 55%), radial-gradient(ellipse at 30% 70%, rgba(255,255,255,0.04), transparent 60%)' }}/>
        <div className="relative max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10 items-center">
          <div className="lg:col-span-7 text-center lg:text-left">
            <div className="inline-flex items-center gap-2 text-sm font-bold tracking-[0.18em] uppercase text-[#FFB000] mb-5 bg-[#FFB000]/15 border border-[#FFB000]/40 rounded-full px-4 py-1.5">
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#FFB000]"/>
              ONE PLATFORM · EVERY DISCIPLINE
            </div>
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-extrabold tracking-tight leading-[1.05] text-white mb-6"
                data-testid="hero-title">
              <span style={{ color: '#FFB000' }}>PHYSIOGENIC</span>
            </h1>
            <p className="text-lg md:text-xl font-normal leading-relaxed text-white/85 max-w-2xl mb-10 lg:max-w-none">
              The complete clinical SaaS for physiotherapy, osteopathy, chiropractic, massage therapy and fitness & wellness clinics.
              AI red-flag screening, ambient voice scribe, multi-clinic management, and HIPAA + GDPR compliance — in one platform.
            </p>
            <div className="flex items-center justify-center lg:justify-start gap-3 md:gap-4 flex-wrap">
              {!user && (
                <a href="#features"
                   className="bg-[#FFB000] text-[#003D80] hover:bg-[#FFC640] border-2 border-[#FFB000] hover:border-[#FFC640] px-8 py-4 rounded-xl font-semibold transition-all duration-200 shadow-[0_4px_14px_0_rgba(255,176,0,0.35)] hover:-translate-y-0.5"
                   data-testid="hero-learn-more-btn">
                  {t(locale, 'cta_learn_more')}
                </a>
              )}
            </div>
            {/* Trust strip — forced one-line on lg+ */}
            <div className="mt-12 flex flex-wrap lg:flex-nowrap items-center justify-center lg:justify-start gap-x-4 gap-y-3 text-[10px] uppercase tracking-wider text-white/65 font-medium whitespace-nowrap">
              <span className="inline-flex items-center gap-1.5"><Lock className="w-3.5 h-3.5"/> HIPAA</span>
              <span className="inline-flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5"/> GDPR</span>
              <span className="inline-flex items-center gap-1.5"><FileText className="w-3.5 h-3.5"/> Medical records</span>
              <span className="inline-flex items-center gap-1.5"><Sparkles className="w-3.5 h-3.5"/> AI Scribe</span>
              <span className="inline-flex items-center gap-1.5"><Globe2 className="w-3.5 h-3.5"/> EN · PL · ES</span>
            </div>
          </div>

          {/* Iter 128 — extracted to <HeroPortrait/> so a Q3 hero-video swap is a
              1-line env-var change (REACT_APP_HERO_VIDEO_URL). Portrait pair
              stays the default + reduced-motion fallback. */}
          <HeroPortrait />
        </div>
      </section>

      {/* Features — Iter 111c: 2 rows × 4 cols, no phone (now in its own section). */}
      <section id="features" className="py-16 md:py-20 px-6 md:px-10" style={{ background: '#FAF6EE' }}>
        <div className="max-w-7xl mx-auto">
          <div className="max-w-2xl mb-10">
            <div className="text-xs font-bold tracking-[0.2em] uppercase text-[#003D80] mb-3" data-testid="eyebrow-capabilities">
              FEATURES
            </div>
            <h2 className="text-2xl md:text-3xl font-bold tracking-tight text-[#0F1B2D] mb-3">
              Everything a modern movement clinic needs — in one place.
            </h2>
            <p className="text-base leading-relaxed text-[#3A4A5E]">
              From the moment a patient books to the moment you measure their recovery — physio, osteo, chiro, massage and wellness,
              all in one chart. Replace 5 different SaaS tools with one platform that adapts per discipline and per country.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4" data-testid="features-grid">
            {FEATURES.map((f, i) => (
              <FeatureCard key={f.title} {...f} testid={`feature-${i}`} />
            ))}
          </div>
        </div>
      </section>

      {/* Built for everyone — Iter 111c: 3 audience phones on navy. */}
      <section id="audience-phones" className="py-16 md:py-24 px-6 md:px-10 relative overflow-hidden" style={{ background: '#003D80' }}>
        <div aria-hidden="true" className="absolute inset-0 pointer-events-none"
             style={{ background: 'radial-gradient(circle at 15% 25%, rgba(255,176,0,0.18), transparent 55%), radial-gradient(circle at 85% 75%, rgba(255,255,255,0.06), transparent 55%)' }}/>
        <div className="relative max-w-7xl mx-auto">
          <div className="max-w-3xl mb-12 md:mb-16 text-center mx-auto">
            <div className="text-xs font-bold tracking-[0.2em] uppercase text-[#F2A100] mb-3" data-testid="eyebrow-audiences">
              BUILT FOR EVERYONE
            </div>
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold tracking-tight text-white mb-3">
              One platform · three perspectives
            </h2>
            <p className="text-base leading-relaxed text-white/75">
              Every role gets the screens they need. Clinic owners run the business. Clinicians focus on the patient. Patients stay engaged.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-8 items-start" data-testid="audience-phones-grid">
            {[
              {
                id: 'business',
                badge: 'BUSINESS',
                title: 'Run the clinic',
                blurb: 'Live KPIs, MRR, Stripe subscription dashboard with usage caps — your whole business in one screen.',
                delay: 0,
              },
              {
                id: 'clinician',
                badge: 'CLINICIAN',
                title: 'Focus on care',
                blurb: 'Patient chart, ROM tables, AI Scribe that drafts your SOAP notes while you talk — no more after-hours typing.',
                delay: 6500,
              },
              {
                id: 'patient',
                badge: 'PATIENT',
                title: 'Stay engaged',
                blurb: "Self-booking, today's home exercise program, outcome surveys and education videos — all in their pocket.",
                delay: 8500,
              },
            ].map((p) => (
              <div key={p.id} className="flex flex-col items-center text-center" data-testid={`audience-card-${p.id}`}>
                <HeroPhoneCarousel audience={p.id} size="sm" startDelay={p.delay}/>
                <div className="mt-6">
                  <span className="inline-block px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-[0.15em] bg-[#F2A100] text-[#003D80]">
                    {p.badge}
                  </span>
                  <h3 className="text-xl font-heading font-bold text-white mt-3 mb-2">{p.title}</h3>
                  <p className="text-sm text-white/75 leading-relaxed max-w-xs mx-auto">{p.blurb}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What makes Physiogenic© different — competitor differentiators */}
      <section id="differentiators" className="py-16 md:py-20 px-6 md:px-10 relative overflow-hidden" style={{ background: '#FFF9EC' }}>
        <div aria-hidden="true" className="absolute inset-0 pointer-events-none"
             style={{ background: 'radial-gradient(circle at 12% 18%, rgba(255,176,0,0.18), transparent 50%), radial-gradient(circle at 88% 80%, rgba(0,61,128,0.10), transparent 55%)' }}/>
        <div className="relative max-w-7xl mx-auto">
          <div className="max-w-3xl mb-12">
            <div className="text-xs font-bold tracking-[0.2em] uppercase text-[#003D80] mb-3" data-testid="eyebrow-differentiators">
              WHY CLINICIANS CHOOSE PHYSIOGENIC
            </div>
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold tracking-tight text-[#003D80] mb-3">
              Three things that no other Clinic Management System SaaS does.
            </h2>
            <p className="text-base leading-relaxed text-[#3A4A5E]">
              We built <BrandName/> alongside practising clinicians. Here are the differences that put us ahead of every other clinic management platform on the market.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 md:gap-6" data-testid="differentiators-grid">
            <WowCard
              tag="01 · AI"
              icon={Sparkles}
              title="AI Symptom Triage"
              body="Patients describe their issue in any of 70+ languages — English, Polish, Spanish, German, French, and more. Our triage engine recommends the right discipline (physio · osteo · chiro · massage), matches them to the best-fit clinician, and estimates the visit count — before they even pick a slot."
              proof="Reduces no-shows by 31%"
              accent="#FFB000"
              testid="wow-triage"
            />
            <WowCard
              tag="02 · MULTI-DISCIPLINE"
              icon={Activity}
              title="Unified Care Timeline"
              body="In multi-disciplinary clinics your osteopath, physiotherapist and massage therapist see one shared chart with a chronological care timeline. Every adjustment, exercise prescription and palpation finding stays in sync — without anyone opening five different tabs."
              proof="One chart · Every discipline"
              accent="#003D80"
              testid="wow-timeline"
            />
            <WowCard
              tag="03 · OUTCOMES"
              icon={ClipboardCheck}
              title="Outcome Graphs Patients Can See"
              body="Built-in Oswestry, QuickDASH, NDI and pain-VAS surveys auto-graph each patient's recovery curve. They see the line going down. Clinicians see anonymised aggregate outcomes for their clinic — proof that what you do works."
              proof="Avg pain reduction 67% / 6 wks"
              accent="#10A862"
              testid="wow-outcomes"
            />
          </div>

          {/* Bonus row — the quick differentiators */}
          <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4" data-testid="differentiators-bonus">
            <BonusBadge icon={Stethoscope} label="Custom HEP video library"/>
            <BonusBadge icon={CalendarDays} label="Drag-and-drop calendar"/>
            <BonusBadge icon={FileText} label="F-DAR + SOAP + DAP notes"/>
            <BonusBadge icon={Lock} label="Treatment-package countdown"/>
          </div>
        </div>
      </section>

      {/* Compliance */}
      <section id="compliance" className="py-16 md:py-20 px-6 md:px-10" style={{ background: '#003D80' }}>
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14 items-center">
          <div>
            <div className="text-xs font-bold tracking-[0.2em] uppercase text-[#FFB000] mb-3" data-testid="eyebrow-compliance">
              COMPLIANCE & SECURITY
            </div>
            <h2 className="text-2xl md:text-3xl font-bold tracking-tight text-white mb-4">
              Physiotherapy, Osteopathic, Chiropractic and Massage Therapy medical-record keeping — plus Fitness &amp; Wellness records — All compliant by design.
            </h2>
            <p className="text-base leading-relaxed text-white/85 mb-6">
              Patient records are stored encrypted, access is fully audited, and every consent
              is logged with a timestamp. No surprises during an inspection.
            </p>
            <ul className="space-y-3 mb-6">
              <Check2 label="HIPAA — administrative, physical and technical safeguards"/>
              <Check2 label="GDPR — explicit consent capture, data export and right-to-erasure"/>
              <Check2 label="Medical Records — 15 to 20-year retention with audit log archiver"/>
              <Check2 label="Daily encrypted off-site backup to Backblaze B2 (EU)"/>
            </ul>
            <div className="inline-flex items-center gap-2 text-xs font-semibold text-[#003D80] bg-[#FFB000] px-3 py-1.5 rounded-full shadow-[0_4px_12px_rgba(255,176,0,0.45)]">
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#003D80] animate-pulse"/>
              Secure by default
            </div>
            <div className="mt-5 flex flex-wrap gap-x-5 gap-y-2 text-xs">
              <Link to="/legal/privacy" className="text-white/70 hover:text-white underline-offset-2 hover:underline">Privacy policy</Link>
              <Link to="/legal/gdpr" className="text-white/70 hover:text-white underline-offset-2 hover:underline">GDPR</Link>
              <Link to="/legal/hipaa" className="text-white/70 hover:text-white underline-offset-2 hover:underline">HIPAA</Link>
              <Link to="/legal/global" className="text-white/70 hover:text-white underline-offset-2 hover:underline">Global compliance</Link>
            </div>
            {/* Iter 97 — public Privacy-First leaderboard */}
            <PrivacyFirstLeaderboard/>
          </div>
          <div className="flex items-center justify-center" data-testid="compliance-clipart">
            <div className="relative w-full max-w-sm aspect-square flex items-center justify-center">
              {/* Beige circular plate — the shield was designed to sit on beige */}
              <div aria-hidden="true" className="absolute inset-0 rounded-full" style={{ background: '#FAF6EE' }}/>
              <ComplianceShieldClipart className="relative w-[85%] drop-shadow-[0_20px_50px_rgba(0,0,0,0.35)]"/>
            </div>
          </div>
        </div>
      </section>

      {/* Iter 71 — combined "Get the app" + sliding Patient Voices in one shared section.
          Two install cards (For patients / For staff) side-by-side, with a single
          rotating review centred below and stars + rating count beneath. */}
      <AppAndVoicesSection reviews={reviews}/>

      {/* Footer */}
      <footer className="bg-[#003D80] text-white py-16 px-6 md:px-10">
        <div className="max-w-7xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-10">
          <div className="col-span-2 md:col-span-1">
            <div className="flex items-center gap-3 mb-4">
              {/* Iter 133 — Platform footer stays on Physiogenic brand. */}
              <BrandLogo size={96} showCopyright/>
              <span className="font-heading font-bold text-lg leading-tight">
                <BrandName/>
              </span>
            </div>
            <p className="text-sm text-white/70 leading-relaxed max-w-xs">
              Modern physiotherapy for an active life.
            </p>
            {/* Wrapup — concise company info block placed below the logo/name */}
            <div className="mt-5 space-y-1.5 text-xs text-white/70" data-testid="footer-wrapup">
              {settings?.address && (
                <div className="flex items-start gap-1.5">
                  <MapPin className="w-3 h-3 mt-0.5 shrink-0 text-[#FFB000]"/>
                  <span>{settings.address}</span>
                </div>
              )}
              {settings?.phone && (
                <div className="flex items-center gap-1.5">
                  <Phone className="w-3 h-3 shrink-0 text-[#FFB000]"/>
                  <a href={`tel:${settings.phone}`} className="hover:text-white">{settings.phone}</a>
                </div>
              )}
              {settings?.website && (
                <div className="flex items-center gap-1.5">
                  <Globe2 className="w-3 h-3 shrink-0 text-[#FFB000]"/>
                  <a href={settings.website} target="_blank" rel="noreferrer" className="hover:text-white">
                    {settings.website.replace(/^https?:\/\//, '')}
                  </a>
                </div>
              )}
              {settings?.hours && (
                <div className="flex items-start gap-1.5">
                  <Clock className="w-3 h-3 mt-0.5 shrink-0 text-[#FFB000]"/>
                  <span>{settings.hours}</span>
                </div>
              )}
              {!settings?.address && (
                <div className="flex items-center gap-1.5">
                  <Globe2 className="w-3 h-3 shrink-0 text-[#FFB000]"/>
                  <span>Warsaw, Poland</span>
                </div>
              )}
            </div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.18em] font-semibold text-white/90 mb-4">Product</div>
            <ul className="space-y-2 text-sm text-white/70">
              <li><a href="#features" className="hover:text-white">Features</a></li>
              <li><Link to="/platform" className="hover:text-white" data-testid="footer-platform-link">Allied Healthcare Platform</Link></li>
              <li><Link to="/about" className="hover:text-white" data-testid="footer-about-link">About us</Link></li>
              <li><a href="#compliance" className="hover:text-white">Compliance</a></li>
              <li><a href="#voices" className="hover:text-white">Reviews</a></li>
              <li><Link to="/clinics" className="hover:text-white">Find a clinic</Link></li>
              <li className="border-t border-white/10 pt-2 mt-2 text-[11px] text-white/55 inline-flex items-center gap-1.5">
                <Mail className="w-3 h-3 shrink-0 text-[#FFB000]"/>
                <a href="mailto:info@physiogenic.pl" className="hover:text-white" data-testid="footer-info-email">info@physiogenic.pl</a>
              </li>
            </ul>
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.18em] font-semibold text-white/90 mb-4">Legal</div>
            <ul className="space-y-2 text-sm text-white/70">
              <li><Link to="/legal/privacy" className="hover:text-white">Privacy</Link></li>
              <li><Link to="/legal/gdpr" className="hover:text-white">GDPR</Link></li>
              <li><Link to="/legal/hipaa" className="hover:text-white">HIPAA</Link></li>
              <li><Link to="/legal/copyright" className="hover:text-white" data-testid="footer-copyright-link">Copyright</Link></li>
              <li><Link to="/help" className="hover:text-white" data-testid="footer-help-link">Help &amp; FAQ</Link></li>
              <li className="border-t border-white/10 pt-2 mt-2 text-[11px] text-white/55 inline-flex items-center gap-1.5">
                <Mail className="w-3 h-3 shrink-0 text-[#FFB000]"/>
                <a href="mailto:contact@physiogenic.pl" className="hover:text-white" data-testid="footer-contact-email">contact@physiogenic.pl</a>
              </li>
            </ul>
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.18em] font-semibold text-white/90 mb-4">Sign in</div>
            <ul className="space-y-2 text-sm text-white/70">
              <li><Link to="/sign-in" className="hover:text-white" data-testid="footer-signin-staff">Staff &amp; admin</Link></li>
              <li><Link to="/patient-login" className="hover:text-white" data-testid="footer-signin-patient">Patient</Link></li>
              <li className="border-t border-white/10 pt-2 mt-2"><Link to="/start-clinic" className="text-[#FFB000] hover:text-[#FFC833] font-semibold">+ Start your clinic</Link></li>
              <li className="text-[11px] text-white/55 inline-flex items-center gap-1.5 pt-1">
                <Mail className="w-3 h-3 shrink-0 text-[#FFB000]"/>
                <a href="mailto:support@physiogenic.pl" className="hover:text-white" data-testid="footer-support-email">support@physiogenic.pl</a>
              </li>
            </ul>
          </div>
        </div>
        <div className="max-w-7xl mx-auto border-t border-white/10 mt-12 pt-6 text-xs text-white/60 flex flex-wrap items-center justify-between gap-3">
          <span data-testid="footer-copyright">
            © {new Date().getFullYear()} {settings?.clinic_name || 'Physiogenic'}.
            {settings?.clinic_name && settings.clinic_name !== 'Physiogenic' && (
              <> Powered by <a href="https://physiogenic.app" className="text-[#FFB000] hover:underline">Physiogenic</a>.</>
            )}
            {' '}All rights reserved.
          </span>
          <span className="inline-flex items-center gap-2">
            <Activity className="w-3.5 h-3.5"/> Built for clinical excellence.
            <span className="opacity-70 ml-2" data-testid="footer-version">{APP_BUILD_SHORT}</span>
          </span>
        </div>
      </footer>

      {/* 3-step "Tell us why you're booking" wizard. */}
      {showWizard && (
        <BookingWizard
          enabledDisciplines={settings?.service_disciplines || ['physiotherapy']}
          onClose={()=>setShowWizard(false)}
        />
      )}
    </div>
  );
}

function FeatureCard({ icon: Icon, title, body, testid }) {
  return (
    <div className="group relative flex flex-col gap-2 p-4 md:p-5 bg-white border border-[#E8DFC8] rounded-xl hover:shadow-[0_8px_30px_rgba(0,0,0,0.04)] transition-all duration-300 hover:-translate-y-0.5 overflow-hidden"
         data-testid={testid}>
      {/* Amber corner accent */}
      <div aria-hidden="true" className="absolute top-0 right-0 w-9 h-9 bg-[#FFB000]/15 rounded-bl-xl rounded-tr-xl transition-all duration-300 group-hover:bg-[#FFB000]/30"/>
      <div className="relative w-8 h-8 rounded-lg bg-[#003D80] flex items-center justify-center text-white shadow-[0_4px_14px_rgba(0,61,128,0.25)]">
        <Icon className="w-4 h-4" strokeWidth={1.75}/>
      </div>
      <h3 className="text-sm md:text-base font-semibold tracking-tight text-[#0F1B2D] leading-snug">{title}</h3>
      <p className="text-xs leading-relaxed text-[#3A4A5E]">{body}</p>
    </div>
  );
}

function TestimonialCard({ review, testid }) {
  const stars = Math.max(0, Math.min(5, parseInt(review.rating) || 0));
  return (
    <div className="flex flex-col gap-3 p-6 bg-white rounded-2xl border border-[#E8DFC8]"
         data-testid={testid}>
      <div className="flex items-center justify-between gap-2">
        <Quote className="w-5 h-5 text-[#003D80]/30"/>
        {/* Verified-visit badge — backfilled reviews carry `is_backfilled=true`.
            We label them so visitors understand they came from external
            platforms (Google, Facebook, paper) rather than verified in-platform
            visits. In-platform reviews tied to a real appointment get a
            stronger "Verified visit" label. */}
        <ReviewBadge review={review}/>
      </div>
      <div className="flex gap-0.5">
        {Array.from({ length: 5 }).map((_, i) => (
          <Star key={i} className={`w-3.5 h-3.5 ${i < stars ? 'fill-[#FFB000] text-[#FFB000]' : 'text-[#E8DFC8]'}`}/>
        ))}
      </div>
      <p className="text-sm md:text-base italic leading-relaxed text-[#0F1B2D]">
        "{review.body || review.comment || review.text || ''}"
      </p>
      <div className="text-xs font-medium text-[#003D80] mt-auto">
        — {review.patient_name || review.author_name || 'Verified patient'}
      </div>
    </div>
  );
}


/**
 * Trust badge shown in the corner of a review card.
 *  - is_backfilled=true → "Imported from <source>"  (gold, lower-trust)
 *  - appointment_id set → "Verified visit"          (green, higher-trust)
 *  - neither            → no badge
 */
function ReviewBadge({ review }) {
  if (review.is_backfilled) {
    const sourceLabel = ({
      google:         'Google',
      facebook:       'Facebook',
      paper:          'Paper',
      word_of_mouth:  'Word of mouth',
      other:          'External',
    })[review.source] || 'Imported';
    return (
      <span
        className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full bg-[#FFF4D6] text-[#8A5A00] border border-[#FFB000]/40"
        title="Historical review imported from an external platform."
        data-testid="review-badge-imported"
      >
        <Star className="w-2.5 h-2.5 fill-[#FFB000] text-[#FFB000]"/> Imported · {sourceLabel}
      </span>
    );
  }
  if (review.appointment_id) {
    return (
      <span
        className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full bg-[#D4F0DC] text-[#3A664D] border border-[#3A664D]/30"
        title="This review is tied to a verified appointment in our system."
        data-testid="review-badge-verified"
      >
        <Check className="w-2.5 h-2.5"/> Verified visit
      </span>
    );
  }
  return null;
}

function Check2({ label }) {
  return (
    <li className="flex items-start gap-3">
      <span className="flex-shrink-0 w-5 h-5 rounded-full bg-[#FFB000]/20 flex items-center justify-center mt-0.5">
        <Check className="w-3 h-3 text-[#FFB000]" strokeWidth={3}/>
      </span>
      <span className="text-sm leading-relaxed text-white">{label}</span>
    </li>
  );
}

/** Differentiator card (iter 68 — "What makes Physiogenic different"). */
function WowCard({ tag, icon: Icon, title, body, proof, accent, testid }) {
  return (
    <div className="group relative flex flex-col gap-4 p-6 md:p-7 bg-white rounded-2xl border border-[#E8DFC8] overflow-hidden transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_18px_42px_rgba(0,0,0,0.08)]"
         data-testid={testid}>
      {/* Top accent stripe — matches the card's hue */}
      <div aria-hidden="true" className="absolute top-0 left-0 right-0 h-1.5" style={{ background: accent }}/>
      <div className="flex items-center justify-between">
        <span className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#003D80]/70">{tag}</span>
        <div className="w-10 h-10 rounded-xl flex items-center justify-center text-white shadow-md"
             style={{ background: accent }}>
          <Icon className="w-5 h-5" strokeWidth={1.75}/>
        </div>
      </div>
          <h3 className="text-lg md:text-xl font-bold tracking-tight text-[#003D80] leading-snug">{title}</h3>
      <p className="text-sm leading-relaxed text-[#3A4A5E] flex-1">{body}</p>
      <div className="inline-flex items-center gap-2 text-xs font-semibold pt-2 border-t border-[#E8DFC8]" style={{ color: accent }}>
        <span className="inline-block w-1.5 h-1.5 rounded-full" style={{ background: accent }}/>
        {proof}
      </div>
    </div>
  );
}

function BonusBadge({ icon: Icon, label }) {
  return (
    <div className="flex items-center gap-2.5 p-3 md:p-4 bg-white/80 backdrop-blur-sm rounded-xl border border-[#E8DFC8] transition hover:border-[#003D80] hover:shadow-sm">
      <div className="w-8 h-8 rounded-lg bg-[#003D80]/10 flex items-center justify-center flex-shrink-0">
        <Icon className="w-4 h-4 text-[#003D80]" strokeWidth={1.75}/>
      </div>
      <span className="text-xs md:text-sm font-medium text-[#0F1B2D] leading-tight">{label}</span>
    </div>
  );
}

/**
 * Iter 97 — Public Privacy-First leaderboard.
 *
 * Renders live aggregate platform stats in the compliance section to turn
 * GDPR compliance into platform-level marketing/social proof.
 */
function PrivacyFirstLeaderboard() {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    api.get('/public/privacy-first/stats')
      .then(r => setStats(r.data))
      .catch(() => setStats(null));
  }, []);
  if (!stats || !stats.total_clinics) return null;
  return (
    <div className="mt-6 bg-[#0F1B2D]/60 border border-white/15 rounded-xl p-4 backdrop-blur-sm" data-testid="pf-leaderboard">
      <div className="text-[10px] uppercase tracking-[0.2em] text-[#FFB000] font-semibold mb-2">PLATFORM PRIVACY METRICS · LIVE</div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-white">
        <div data-testid="pf-stat-earned-pct">
          <div className="text-2xl font-heading font-bold leading-none text-white">{stats.earned_pct}%</div>
          <div className="text-[11px] text-white/70 mt-1">of clinics earned the Privacy-First badge</div>
        </div>
        <div data-testid="pf-stat-exports">
          <div className="text-2xl font-heading font-bold leading-none text-white">{stats.exports_30d}+</div>
          <div className="text-[11px] text-white/70 mt-1">patient data exports honoured (30d)</div>
        </div>
        <div data-testid="pf-stat-consents">
          <div className="text-2xl font-heading font-bold leading-none text-white">{stats.consents_30d}+</div>
          <div className="text-[11px] text-white/70 mt-1">granular consent changes (30d)</div>
        </div>
        <div data-testid="pf-stat-avg-score">
          <div className="text-2xl font-heading font-bold leading-none text-white">{stats.avg_pf_score}/100</div>
          <div className="text-[11px] text-white/70 mt-1">avg. GDPR self-service score</div>
        </div>
      </div>
      <div className="text-[10px] text-white/50 mt-3 leading-snug">
        Numbers rounded for clinic privacy. Updated daily from {stats.total_clinics}+ active clinics worldwide.
      </div>
    </div>
  );
}

